import React from 'react';
import { Wrench, ArrowRight, Car, FileText, CreditCard, BellRing, Globe, Cloud, Check, Star } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const HERO = 'https://static.prod-images.emergentagent.com/jobs/75b687c8-f7df-4329-a48f-22005a0b95db/images/909f2aa5ba8438f549f3e06cdf5dc30525a9a639c23dc6821d2e9930141591d6.png';

const go = (url) => () => { window.location.href = url; };

const STATS = [
  { icon: Car, label: '15+ modules' },
  { icon: FileText, key: 'Facturen in < 5 minuten' },
  { icon: CreditCard, key: 'Online betalen via Stripe' },
  { icon: BellRing, key: 'Automatische APK-herinneringen' },
  { icon: Globe, key: 'Meertalig' },
  { icon: Cloud, key: 'Cloud-back-ups' },
];

export default function LandingPage() {
  const { t, lang, setLang, languages } = useT();
  useDocumentMeta(
    'MonteurCentraal — Complete garagesoftware voor moderne werkplaatsen',
    'Beheer je hele werkplaats op één plek: klanten, voertuigen, offertes & facturatie, planning, boekhouding, e-facturatie en online afspraken. Probeer MonteurCentraal — gratis voor hobbyisten.',
    '/',
  );
  const benefits = [
    t('Minder administratie — alles op één plek.', 'Minder administratie — alles op één plek.'),
    t('Sneller factureren: een complete factuur in minder dan 5 minuten.', 'Sneller factureren: een complete factuur in minder dan 5 minuten.'),
    t('Meer terugkerende klanten dankzij automatische APK- en reviewherinneringen.', 'Meer terugkerende klanten dankzij automatische APK- en reviewherinneringen.'),
    t('Direct online betaald worden via iDEAL en creditcard.', 'Direct online betaald worden via iDEAL en creditcard.'),
    t('Gratis te gebruiken voor hobbyisten.', 'Gratis te gebruiken voor hobbyisten.'),
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0b1220', color: '#e2e8f0', fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }} data-testid="landing-page">
      {/* Nav */}
      <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px clamp(16px, 5vw, 64px)', maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 800, fontSize: 20 }}>
          <span style={{ width: 36, height: 36, borderRadius: 10, background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Wrench size={20} color="#fff" /></span>
          MonteurCentraal
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {languages.length > 1 && (
            <select value={lang} onChange={e => setLang(e.target.value)} data-testid="landing-lang"
              style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, color: '#fff', padding: '7px 8px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              {languages.map(l => <option key={l.code} value={l.code} style={{ color: '#000' }}>{(l.code || '').toUpperCase()}</option>)}
            </select>
          )}
          <button onClick={go('/?login=1')} data-testid="landing-login" className="mc-btn-ghost"
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.18)', color: '#e2e8f0', padding: '9px 16px', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
            {t('Inloggen', 'Inloggen')}
          </button>
          <button onClick={go('/?register=1')} data-testid="landing-cta-start" className="mc-btn-primary"
            style={{ background: '#2563eb', border: 'none', color: '#fff', padding: '9px 18px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            {t('Gratis starten', 'Gratis starten')}
          </button>
        </div>
      </nav>

      {/* Hero */}
      <header style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(24px, 5vw, 64px) clamp(16px, 5vw, 64px)', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 48, alignItems: 'center' }} className="mc-hero">
        <div className="mc-fade">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(37,99,235,.12)', border: '1px solid rgba(37,99,235,.35)', color: '#93c5fd', padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, marginBottom: 20 }}>
            <Star size={14} /> {t('Gratis voor hobbyisten', 'Gratis voor hobbyisten')}
          </div>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', lineHeight: 1.05, margin: '0 0 18px', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
            {t('Binnen 5 minuten je factuur versturen.', 'Binnen 5 minuten je factuur versturen.')}
          </h1>
          <p style={{ fontSize: 'clamp(15px, 1.5vw, 18px)', color: '#94a3b8', maxWidth: 520, margin: '0 0 28px', lineHeight: 1.6 }}>
            {t('Het complete werkplaatssysteem voor de moderne garage: klanten, voertuigen, facturen, planning, online afspraken en betalingen — alles op één plek.', 'Het complete werkplaatssysteem voor de moderne garage: klanten, voertuigen, facturen, planning, online afspraken en betalingen — alles op één plek.')}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={go('/?register=1')} data-testid="hero-cta-start" className="mc-btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#2563eb', border: 'none', color: '#fff', padding: '14px 26px', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 16 }}>
              {t('Gratis starten', 'Gratis starten')} <ArrowRight size={18} />
            </button>
            <button onClick={go('/pricing')} data-testid="hero-cta-pricing"
              style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.16)', color: '#e2e8f0', padding: '14px 26px', borderRadius: 12, fontWeight: 600, cursor: 'pointer', fontSize: 16 }}>
              {t('Prijzen bekijken', 'Prijzen bekijken')}
            </button>
          </div>
        </div>
        <div className="mc-fade mc-hero-img" style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', inset: -40, background: 'radial-gradient(circle at 70% 30%, rgba(37,99,235,.35), transparent 60%)', filter: 'blur(20px)' }} />
          <img src={HERO} alt="MonteurCentraal in de werkplaats" loading="eager"
            style={{ position: 'relative', width: '100%', borderRadius: 18, border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 30px 80px rgba(0,0,0,.5)', display: 'block' }} />
        </div>
      </header>

      {/* Stats row */}
      <section style={{ borderTop: '1px solid rgba(255,255,255,.08)', borderBottom: '1px solid rgba(255,255,255,.08)', background: 'rgba(255,255,255,.02)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px clamp(16px, 5vw, 64px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16 }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#cbd5e1', fontWeight: 600 }} data-testid={`stat-${i}`}>
              <span style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(37,99,235,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <s.icon size={18} color="#60a5fa" />
              </span>
              {s.label || t(s.key, s.key)}
            </div>
          ))}
        </div>
      </section>

      {/* Why */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(40px, 6vw, 80px) clamp(16px, 5vw, 64px)' }}>
        <h2 style={{ fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>{t('Waarom MonteurCentraal?', 'Waarom MonteurCentraal?')}</h2>
        <p style={{ color: '#94a3b8', margin: '0 0 36px', fontSize: 16 }}>{t('Geen lange featurelijst — gewoon de voordelen die jouw werkplaats verder helpen.', 'Geen lange featurelijst — gewoon de voordelen die jouw werkplaats verder helpen.')}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
          {benefits.map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 14, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 14, padding: 20 }} data-testid={`benefit-${i}`}>
              <span style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(34,197,94,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Check size={17} color="#4ade80" />
              </span>
              <span style={{ fontSize: 15.5, color: '#e2e8f0', lineHeight: 1.5 }}>{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(16px, 5vw, 64px) clamp(48px, 6vw, 90px)' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e40af, #2563eb)', borderRadius: 22, padding: 'clamp(32px, 5vw, 56px)', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>{t('Klaar om sneller te factureren?', 'Klaar om sneller te factureren?')}</h2>
          <p style={{ color: 'rgba(255,255,255,.85)', margin: '0 0 28px', fontSize: 17 }}>{t('Begin gratis. Geen creditcard nodig.', 'Begin gratis. Geen creditcard nodig.')}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={go('/?register=1')} data-testid="bottom-cta-start"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', border: 'none', color: '#1e40af', padding: '14px 30px', borderRadius: 12, fontWeight: 800, cursor: 'pointer', fontSize: 16 }}>
              {t('Gratis starten', 'Gratis starten')} <ArrowRight size={18} />
            </button>
            <button onClick={go('/pricing')} data-testid="bottom-cta-pricing"
              style={{ background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.3)', color: '#fff', padding: '14px 30px', borderRadius: 12, fontWeight: 600, cursor: 'pointer', fontSize: 16 }}>
              {t('Prijzen bekijken', 'Prijzen bekijken')}
            </button>
          </div>
        </div>
      </section>

      <footer style={{ borderTop: '1px solid rgba(255,255,255,.08)', padding: '24px clamp(16px, 5vw, 64px)', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
        © {new Date().getFullYear()} MonteurCentraal ·{' '}
        <a href="/voorwaarden" style={{ color: '#94a3b8' }}>{t('Voorwaarden', 'Voorwaarden')}</a> ·{' '}
        <a href="/privacy" style={{ color: '#94a3b8' }}>{t('Privacy', 'Privacy')}</a>
      </footer>

      <style>{`
        .mc-fade { animation: mcFade .6s ease both; }
        .mc-hero-img { animation-delay: .12s; }
        @keyframes mcFade { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .mc-btn-primary { transition: transform .15s ease, background-color .15s ease; }
        .mc-btn-primary:hover { transform: translateY(-1px); background:#1d4ed8; }
        .mc-btn-ghost { transition: background-color .15s ease; }
        .mc-btn-ghost:hover { background: rgba(255,255,255,.1); }
        @media (max-width: 860px) { .mc-hero { grid-template-columns: 1fr !important; } .mc-hero-img { order: -1; } }
      `}</style>
    </div>
  );
}
